import { CRUDStarterInterface } from './crud-starter.interface';
import { CRUDStarterAndReturnInterface } from './crud-starter-and-return.interface';
import { IframeCRUDStarterAndReturnInterface } from './iframe-crud-starter-and-return.interface';
import { AuthService } from '../auth/auth.service';
import { RoutingService } from '../routing/routing.service';
import { LongOpStarterInterface } from './long-op-starter.interface';
import { Message, TabSocket, StatusMessageInterface } from '@nts/std/utility';
import { IFrameGetter, UIStarter } from './ui-starter';
import { PostMessageBridgeInterface, MessageType } from '@nts/std/utility';
import { ActivityStarterInterface } from './activity-starter.interface';
import { take, takeUntil } from 'rxjs';

export class BaseUIStarter implements ActivityStarterInterface, LongOpStarterInterface, CRUDStarterInterface, CRUDStarterAndReturnInterface, IframeCRUDStarterAndReturnInterface {

    protected _bridge: PostMessageBridgeInterface;

    constructor(
        private authService: AuthService,
        private routingService: RoutingService,
    ) { }

    updateCurrentRoute(
        rootModelFullName?: string,
        jsonIdentity?: string,
        relativeUrl?: string,
        queryString?: string,
        internalRelativeUrl?: string
    ) {
        this.routingService.updateCurrentRoute(
            rootModelFullName,
            jsonIdentity,
            relativeUrl,
            queryString,
            internalRelativeUrl
        );
    }

    async connectReadAndReturn(
        iframeBaseUrl: string,
        jsonIdentity: string,
        additionalQueryParams: URLSearchParams = new URLSearchParams(),
        returnAction: (payload: Message<string>) => void,
        errorAction: (identity: string) => void,
        iframeGetter: IFrameGetter,
        externalReturn = true,
        statusCallback?: (status: StatusMessageInterface) => void
    ): Promise<TabSocket> {
        const identityParam: string = RoutingService.encodeUrl(jsonIdentity);

        additionalQueryParams = additionalQueryParams ?? new URLSearchParams();
        additionalQueryParams.append('iframe', 'true');

        if (externalReturn) {
            additionalQueryParams.append('external-return', 'true');
        }

        let fullUrlToOpen: string = iframeBaseUrl + '/' + identityParam + '?' + additionalQueryParams.toString();
        fullUrlToOpen = await this.authService.getUrlWithTokens(fullUrlToOpen);
        const iframeData = await iframeGetter(fullUrlToOpen);
        return this.socketListen(iframeData.tabSocket, returnAction, errorAction, statusCallback);
    }

    async showReadAndReturn(
        url: string,
        jsonIdentity: string,
        additionalQueryParams: URLSearchParams = new URLSearchParams(),
        returnAction: (payload: Message<string>) => void,
        errorAction: (message: string) => void,
        statusCallback?: (status: StatusMessageInterface) => void
    ): Promise<TabSocket> {
        let openedWindow: Window;

        const identityParam: string = RoutingService.encodeUrl(jsonIdentity);

        additionalQueryParams = additionalQueryParams ?? new URLSearchParams();
        additionalQueryParams.append('external-return', 'true');

        let fullUrlToOpen: string = url + '/' + identityParam + '?' + additionalQueryParams.toString();

        fullUrlToOpen = await this.authService.getUrlWithTokens(fullUrlToOpen);

        openedWindow = window.open(fullUrlToOpen);

        const tabSocket: TabSocket = new TabSocket(UIStarter.zone, openedWindow);
        return this.socketListen(tabSocket, returnAction, errorAction, statusCallback);
    }

    async showNewAndReturn(
        url: string,
        additionalQueryParams: URLSearchParams = new URLSearchParams(),
        returnAction: (payload: Message<string>) => void,
        errorAction: (identity: string) => void,
        statusCallback?: (status: StatusMessageInterface) => void
    ): Promise<TabSocket> {
        let openedWindow: Window;
        const concatenateOperator: string = url.split('?').length > 1 ? '&' : '?';

        additionalQueryParams = additionalQueryParams ?? new URLSearchParams();
        additionalQueryParams.append('external-return', 'true');

        let fullUrlToOpen: string = url + concatenateOperator + additionalQueryParams.toString();
        fullUrlToOpen = await this.authService.getUrlWithTokens(fullUrlToOpen);
        openedWindow = window.open(fullUrlToOpen);

        const tabSocket: TabSocket = new TabSocket(UIStarter.zone, openedWindow);
        return this.socketListen(tabSocket, returnAction, errorAction, statusCallback);
    }

    async startOtherDomainWithTokens(
        url: string,
        blank: boolean
    ) {
        const fullUrlToOpen: string = await this.authService.getUrlWithTokens(url);
        window.open(fullUrlToOpen, blank ? '_blank' : '_self');
    }

    async connectNewAndReturn(
        iframeBaseUrl: string,
        additionalQueryParams: URLSearchParams = new URLSearchParams(),
        returnAction: (payload: Message<string>) => void,
        errorAction: (identity: string) => void,
        iframeGetter: IFrameGetter,
        externalReturn = true,
        statusCallback?: (status: StatusMessageInterface) => void
    ): Promise<TabSocket> {
        const concatenateOperator: string = iframeBaseUrl.split('?').length > 1 ? '&' : '?';

        additionalQueryParams = additionalQueryParams ?? new URLSearchParams();
        additionalQueryParams.append('iframe', 'true');

        if (externalReturn) {
            additionalQueryParams.append('external-return', 'true');
        }

        let fullUrlToOpen: string = iframeBaseUrl + concatenateOperator + additionalQueryParams.toString();
        fullUrlToOpen = await this.authService.getUrlWithTokens(fullUrlToOpen);
        const iframeWindow = await iframeGetter(fullUrlToOpen);

        return this.socketListen(iframeWindow.tabSocket, returnAction, errorAction, statusCallback);
    }

    redirectToLogin(clearAuthData: boolean = false, clearRedirectUrl: boolean = false): void {
        this.routingService.redirectToLogin(null, clearAuthData, clearRedirectUrl);
    }

    redirectToChooseTenant() {
        this.routingService.redirectToChooseTenant();
    }

    redirectToChangePassword(blank = false) {
        this.routingService.redirectToChangePassword(blank);
    }

    async redirectToDashBoard(blank = false): Promise<void> {
        this.routingService.redirectToDashBoard(blank);
    }

    showReadRelated(
        relatedDomainModelFullName: string,
        url: string,
        jsonIdentity: string,
        additionalQueryParams: URLSearchParams,
        isRoot = false,
        blank = false
    ): void {
        this.routingService.navigateInRelated(
            relatedDomainModelFullName,
            url,
            jsonIdentity,
            additionalQueryParams,
            isRoot,
            blank
        );
    }

    startCrud(
        relatedDomainModelFullName: string,
        url: string,
        jsonIdentity?: string,
        additionalQueryParams?: URLSearchParams,
        addHistoryBackButton?: boolean,
        blank = false
    ): void {
        this.routingService.navigateInCrud(
            relatedDomainModelFullName,
            url,
            jsonIdentity,
            additionalQueryParams,
            addHistoryBackButton,
            blank
        );
    }

    startLongOp(
        domainModelFullName: string,
        url: string,
        jsonObject?: string,
        additionalQueryParams?: URLSearchParams,
        addHistoryBackButton?: boolean,
        blank = false
    ): void {
        this.routingService.navigateInLongOp(
            domainModelFullName,
            url,
            jsonObject,
            additionalQueryParams,
            addHistoryBackButton,
            blank
        );
    }

    startActivity(
        domainModelFullName: string,
        url: string,
        jsonObject?: string,
        additionalQueryParams?: URLSearchParams,
        addHistoryBackButton?: boolean,
        blank = false
    ): void {
        this.routingService.navigateInActivity(
            domainModelFullName,
            url,
            jsonObject,
            additionalQueryParams,
            addHistoryBackButton,
            blank
        );
    }

    notifyClosed() {
        const currentWindow: Window = window;
        currentWindow.close();
    }

    private socketListen(
        tabSocket: TabSocket,
        returnAction: (payload: Message<string>) => void,
        errorAction: (message: string) => void,
        statusCallback?: (status: StatusMessageInterface) => void
    ): TabSocket {


        tabSocket.onClose.pipe(
            take(1) // Automatically unsubscribe after the first event
        ).subscribe(() => {
            if (returnAction) {
                returnAction(new Message('closed-connection', MessageType.ClosedConnection));
            }
            tabSocket.destroy();
        });

        // Use new Observable-based message listener for modern approach
        tabSocket.onMessage<string>(MessageType.ExternalReturn)
            .pipe(
                takeUntil(tabSocket.onClose)
            ).subscribe((message: Message<string>) => {
                if (returnAction) {
                    // Wrap in Message object for consistency with old callback pattern
                    returnAction(new Message(message.data, MessageType.ExternalReturn));
                }
            }, (error) => {
                if (errorAction) {
                    errorAction(error?.message || 'Error receiving message');
                }
            });

        // Optional status tracking via push mechanism
        if (statusCallback) {
            tabSocket.onStatusPush()
                .pipe(
                    takeUntil(tabSocket.onClose)
                ).subscribe((status: StatusMessageInterface) => {
                    if (statusCallback) {
                        statusCallback(status);
                    }
                });
        }
        return tabSocket;
    }
}
